<template>
  <div class="pp-filters">
    <!-- Range presets. Highlighted only when the dates still match the preset,
         so a hand-edited range doesn't leave a stale chip looking active. -->
    <div class="pp-chips">
      <button
        v-for="p in presets"
        :key="p.key"
        type="button"
        class="pp-chip"
        :class="{ 'pp-chip--on': activePreset === p.key }"
        @click="$emit('preset', p.key)"
      >{{ p.label }}</button>
    </div>

    <div class="pp-filter-row">
      <div class="pp-search">
        <i class="fa fa-search pp-search-icon" aria-hidden="true"></i>
        <input
          :value="search"
          type="text"
          class="pp-search-input"
          placeholder="Search patient or transaction…"
          aria-label="Search payments"
          @input="$emit('update:search', $event.target.value)"
        />
      </div>

      <div class="pp-dates">
        <input
          :value="from"
          type="date"
          class="pp-date"
          :max="to"
          aria-label="From date"
          @input="$emit('update:from', $event.target.value)"
        />
        <span class="pp-dates-sep">to</span>
        <input
          :value="to"
          type="date"
          class="pp-date"
          :min="from"
          aria-label="To date"
          @input="$emit('update:to', $event.target.value)"
        />
      </div>

      <div class="pp-segment">
        <button
          v-for="s in syncOptions"
          :key="s.key"
          type="button"
          class="pp-seg"
          :class="{ 'pp-seg--on': syncFilter === s.key }"
          @click="$emit('update:syncFilter', s.key)"
        >{{ s.label }}</button>
      </div>

      <button v-if="anyFilter" type="button" class="pp-clear" @click="$emit('clear')">
        <i class="fa fa-times" aria-hidden="true"></i> Clear filters
      </button>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'PaymentFilters',
  props: {
    presets: { type: Array, required: true },
    activePreset: { type: String, default: null },
    from: { type: String, default: '' },
    to: { type: String, default: '' },
    search: { type: String, default: '' },
    syncFilter: { type: String, default: 'all' },
    anyFilter: { type: Boolean, default: false },
  },
  data() {
    return {
      syncOptions: [
        { key: 'all', label: 'All' },
        { key: 'synced', label: 'Synced' },
        { key: 'issues', label: 'Issues' },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.pp-filters {
  padding: 16px 18px;
  border-bottom: 1px solid $pp-border;
}

.pp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.pp-chip {
  padding: 7px 13px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid $pp-border;
  background: $pp-surface;
  color: $pp-text-muted;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;

  &:hover { background: $pp-brand-subtle; color: $pp-brand-deep; }

  &--on,
  &--on:hover {
    border-color: $pp-brand;
    background: $pp-brand;
    color: #fff;
  }
}

.pp-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.pp-search {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 290px;
}

.pp-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: $pp-text-subtle;
  font-size: 13px;
}

// Plain <input> rather than <b-form-input>: it inherits the scope attribute
// directly, so these styles can't leak and don't need ::v-deep to beat
// Bootstrap's .form-control.
.pp-search-input {
  width: 100%;
  height: 40px;
  padding: 0 12px 0 34px;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;
  font-size: 14px;
  color: $pp-text;
  background: $pp-surface;
  outline: none;

  &::placeholder { color: $pp-text-subtle; }
  &:focus { border-color: $pp-brand; box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12); }
}

.pp-dates {
  display: flex;
  align-items: center;
  gap: 6px;
}

// Native date input per the repo convention (RecallFilterCard.vue), not
// b-form-datepicker.
.pp-date {
  height: 40px;
  padding: 0 10px;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;
  font-size: 13px;
  color: $pp-text;
  background: $pp-surface;
  outline: none;

  &:focus { border-color: $pp-brand; box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12); }
}

.pp-dates-sep {
  font-size: 13px;
  color: $pp-text-muted;
}

.pp-segment {
  display: inline-flex;
  padding: 3px;
  background: $pp-surface-sunken;
  border-radius: $pp-radius-sm;
  border: 1px solid $pp-border;
}

.pp-seg {
  padding: 6px 12px;
  border: 0;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 700;
  color: $pp-text-muted;
  background: transparent;
  cursor: pointer;

  &--on {
    color: $pp-text;
    background: $pp-surface;
    box-shadow: $pp-shadow;
  }
}

.pp-clear {
  margin-left: auto;
  border: 0;
  background: none;
  font-size: 13px;
  font-weight: 600;
  color: $pp-brand;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &:hover { color: $pp-brand-deep; text-decoration: underline; }
}

@media (max-width: 720px) {
  .pp-search { max-width: none; }
  .pp-clear { margin-left: 0; }
}
</style>
